import { useContext } from 'react';
import SessionTimelineContext from '../state/SessionTimelineContext';
import { Customization, AdminSection, Segment, Supply } from '../models/timeline';
import { createSectionCustomization } from '../api/createSectionCustomization';
import { updateSectionCustomization } from '../api/updateSectionCustomization';
import eventBus from '../../../services/globalEventBus';
import { deleteSectionCustomization } from '../api/deleteSectionCustomization';
import sessionTimelineService from '../../../services/sessionTimelineService';

const getPrintPreferenceMap = (crmName: string) => {
  const defaultMap = {
    all: { title: 'All Groups', description: 'Print all group sections' },
    large_group_leader: { title: 'Large Group Only', description: 'Print large group sections only' },
    small_group_leader: { title: 'Small Group Only', description: 'Print small group section only' },
  };

  if (crmName === 'preschool' || crmName === 'kids')
    return {
      ...defaultMap,
      small_group_leader: { title: 'Team Time Only', description: 'Print Team Time section only' },
    };

  return defaultMap;
};

const getViewPreferenceMap = (crmName: string) => {
  const defaultMap = {
    all: { title: 'All Leaders', description: 'Show all group activities' },
    large_group_leader: { title: 'Large Group Only', description: 'Show large group activities only' },
    small_group_leader: { title: 'Small Group Only', description: 'Show small group activities only' },
  };

  if (crmName === 'preschool' || crmName === 'kids')
    return {
      ...defaultMap,
      small_group_leader: { title: 'Team Time Only', description: 'Show Team Time activities only' },
    };

  return defaultMap;
};

const getSegmentGroupMap = (crmName: string) => {
  const defaultMap = {
    leaders: 'Leaders',
    small: 'Small Group',
    large: 'Large Group',
  };

  if (crmName === 'preschool' || crmName === 'kids')
    return {
      ...defaultMap,
      small: 'Team Time',
    };

  return defaultMap;
};

const getSegmentLeaderMap = (crmName: string) => {
  const defaultMap = {
    all: 'All Leaders',
    small_group_leader: 'Small Group Leader',
    large_group_leader: 'Large Group Leader',
  };

  if (crmName === 'preschool' || crmName === 'kids')
    return {
      ...defaultMap,
      small_group_leader: 'Team Time Leader',
    };

  return defaultMap;
};

const segmentActivityMap = {
  Craft: 'Craft',
  Game: 'Game',
  Lesson: 'Lesson',
  Printable: 'Printable',
  'Stage Game': 'Stage Game',
  'Team Game': 'Team Game',
  Transition: 'Transition',
} as const;

const sectionTypeMap = {
  body: { iconClass: 'fas fa-text-height', description: 'Add a section of text, lists or blockquotes' },
  script: { iconClass: 'fas fa-volume-up', description: 'Add a script box to be read out loud' },
  supplies: {
    iconClass: 'fas fa-tasks',
    description: 'Add a list of activity supplies',
    iconType: { craft: 'Craft', game: 'Game', school: 'School' },
  },
  tip: {
    iconClass: 'fas fa-lightbulb',
    description: 'Add a leader or safety tip',
    tipType: { leader: 'Leader Tip', safety: 'Safety Tip' },
  },
  video: { iconClass: 'fab fa-youtube', description: 'Add a section to present a video' },
} as const;

const useSessionTimeline = () => {
  const context = useContext(SessionTimelineContext);
  if (!context) {
    throw new Error('useSessionTimeline must be used within a SessionTimelineProvider');
  }
  const { timeline, setTimeline, curriculumName, session } = context;

  const segmentGroupMap = getSegmentGroupMap(curriculumName);
  const segmentLeaderMap = getSegmentLeaderMap(curriculumName);
  const viewPreferenceMap = getViewPreferenceMap(curriculumName);
  const printPreferenceMap = getPrintPreferenceMap(curriculumName);

  const updateSectionInTimeline = (
    sectionId: string,
    segmentId: string,
    text?: string,
    supplies?: Supply[],
    includeCustomizations: boolean = true
  ) => {
    const updateSections = (sections: AdminSection[], customization: Customization) =>
      sections.map(section =>
        section.id === sectionId
          ? {
              ...section,
              customizations: includeCustomizations ? [customization] : [],
            }
          : section
      );

    const applyCustomizationToTimeline = (customization: Customization) =>
      setTimeline(previousTimeline => ({
        ...previousTimeline,
        segments: previousTimeline.segments.map(segment => {
          if (segment.isFlex && segment.options) {
            return {
              ...segment,
              options: segment.options.map(option => ({
                ...option,
                sections: updateSections(option.sections, customization),
              })),
            };
          }
          return {
            ...segment,
            sections: updateSections(segment.sections, customization),
          };
        }),
      }));

    if (text) {
      applyCustomizationToTimeline({ sectionId, segmentId, text, customizationType: 'text' as const });
    } else if (supplies) {
      applyCustomizationToTimeline({ sectionId, segmentId, supplies, customizationType: 'supplies' as const });
    }
  };

  const customizeSectionContent = (
    section: AdminSection,
    segment: Segment,
    content: { text?: string; supplies?: Supply[] },
    orgId: string,
    initiatingUserId: string
  ) => {
    const isCustomized = section.customizations?.length;
    const customizationType: 'text' | 'supplies' = content.text !== undefined ? 'text' : 'supplies';
    const originalText = section.customizations?.[0]?.text || section.text || '';
    const originalSupplies = section.supplies || [];

    // optimistic update timeline
    if (customizationType === 'text') {
      updateSectionInTimeline(section.id, segment.segmentId, content.text);
    } else {
      updateSectionInTimeline(section.id, segment.segmentId, '', content.supplies);
    }

    return new Promise((resolve, reject) => {
      eventBus.subscribe(
        isCustomized ? 'TcSectionCustomizationUpdated' : 'TcSectionCustomizationCreated',
        (event: { id: string; email: string }) => {
          resolve(event);
        }
      );

      eventBus.subscribe('TcCommandFailed', (event: { reason: { description: string } }) => {
        // Revert the timeline to original state on failure
        if (customizationType === 'text') {
          updateSectionInTimeline(section.id, segment.segmentId, originalText);
        } else {
          updateSectionInTimeline(section.id, segment.segmentId, '', originalSupplies);
        }
        reject(new Error(event.reason.description));
      });

      const customizationData = {
        customizationType,
        segmentID: segment.segmentId,
        sectionID: section.id,
        ...(content.text !== undefined && { text: content.text }),
        ...(content.supplies !== undefined && { supplies: content.supplies }),
      };

      if (isCustomized) {
        updateSectionCustomization(
          timeline.timelineCustomizationId,
          timeline.id,
          customizationData,
          orgId,
          initiatingUserId
        );
      } else {
        createSectionCustomization(
          timeline.timelineCustomizationId,
          timeline.id,
          customizationData,
          orgId,
          initiatingUserId
        );
      }
    });
  };

  const customizeSection = (
    section: AdminSection,
    segment: Segment,
    text: string,
    orgId: string,
    initiatingUserId: string
  ) => {
    return customizeSectionContent(section, segment, { text }, orgId, initiatingUserId);
  };

  const customizeSupplies = (
    section: AdminSection,
    segment: Segment,
    supplies: Supply[],
    orgId: string,
    initiatingUserId: string
  ) => {
    return customizeSectionContent(section, segment, { supplies }, orgId, initiatingUserId);
  };

  const removeSectionCustomization = (
    section: AdminSection,
    segment: Segment,
    orgId: string,
    initiatingUserId: string,
    customizationType: 'text' | 'supplies' = 'text'
  ) => {
    const originalText = section.text || '';
    const customizedText = section.customizations?.[0]?.text || '';
    const originalSupplies = section.supplies || [];
    const customizedSupplies = section.customizations?.[0]?.supplies || [];

    // optimistic update timeline
    if (customizationType === 'text') {
      updateSectionInTimeline(section.id, segment.segmentId, originalText, [], false);
    } else {
      updateSectionInTimeline(section.id, segment.segmentId, '', customizedSupplies);
    }

    return new Promise((resolve, reject) => {
      eventBus.subscribe('TcSectionCustomizationDeleted', (event: { id: string; email: string }) => {
        resolve(event);
      });
      eventBus.subscribe('TcCommandFailed', (event: { reason: { description: string } }) => {
        // Revert the timeline to original state on failure
        if (customizationType === 'text') {
          updateSectionInTimeline(section.id, segment.segmentId, customizedText);
        } else {
          updateSectionInTimeline(section.id, segment.segmentId, '', originalSupplies);
        }
        reject(new Error(event.reason.description));
      });
      deleteSectionCustomization(
        timeline.timelineCustomizationId,
        timeline.id,
        orgId,
        section.id,
        segment.segmentId,
        initiatingUserId
      );
    });
  };

  const updateLeaderViewPreference = (preference: string) => {
    setTimeline(previousTimeline =>
      sessionTimelineService.updateViewPreferenceForTimeline(previousTimeline, preference)
    );

    sessionTimelineService.setLeaderViewPreference(timeline.curriculumId, preference).catch(console.error);
  };

  return {
    curriculumName,
    timeline,
    setTimeline,
    session,
    segmentGroupMap,
    segmentLeaderMap,
    viewPreferenceMap,
    printPreferenceMap,
    segmentActivityMap,
    sectionTypeMap,
    customizeSection,
    customizeSupplies,
    removeSectionCustomization,
    updateLeaderViewPreference,
  };
};

export default useSessionTimeline;
